/**
 * O catálogo de modelos que uma integração declara, separado por finalidade (AB#85605).
 *
 * Fonte única: `metadata.models`, agrupado pelo `api` que cada entrada declara — o endpoint
 * em que aquele modelo responde. Sem esse agrupamento, o campo conversacional sugeria
 * `text-embedding-ada-002`, porque a lista era "todas as chaves" (AB#85434).
 *
 * Ausência vale `chat`, como o gateway sempre leu: é o que a maioria das entradas é, e é o
 * que mantém funcionando cadastro que ainda não foi declarado.
 *
 * Vivia dentro da tela de AI Models da organização. Saiu para cá quando a plataforma passou a
 * escolher três finalidades pela mesma regra — duas cópias divergiriam na primeira vez que um
 * `api` novo aparecesse.
 */
export type ModelPurpose = "conversational" | "embedding" | "transcription" | "web_search";

export type ModelCatalog = Record<ModelPurpose, string[]>;

export const EMPTY_MODEL_CATALOG: ModelCatalog = {
  conversational: [],
  embedding: [],
  transcription: [],
  web_search: [],
};

/**
 * O "modelo" gravado quando o provedor de busca web não tem modelo (Tavily). Espelha
 * `WEB_SEARCH_MODEL_PLACEHOLDER` da api: a linha de `organization_ai_models` exige um.
 */
export const WEB_SEARCH_MODEL_PLACEHOLDER = "search";

/**
 * Adaptadores de busca que são chamadas de LLM: o modelo escolhido é quem pesquisa, então a
 * tela oferece os modelos conversacionais da integração. Espelha `WEB_SEARCH_LLM_ADAPTERS`
 * da api.
 */
const WEB_SEARCH_LLM_ADAPTERS = ["azure_openai_responses", "openrouter_web_search"];

/**
 * O catálogo não tem modelo de verdade a escolher — só o marcador. A tela esconde o campo
 * de modelo e grava o marcador sozinha: perguntar "qual modelo?" da Tavily não tem resposta.
 */
export function isModelless(models: readonly string[]): boolean {
  return models.length === 1 && models[0] === WEB_SEARCH_MODEL_PLACEHOLDER;
}

/**
 * O adaptador de busca web que a integração declara em `metadata.capabilities.web_search`
 * (AB#86885), ou `null` quando ela não faz busca web.
 */
export function readWebSearchAdapter(metadata: unknown): string | null {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) return null;
  const capabilities = (metadata as Record<string, unknown>).capabilities;
  if (!capabilities || typeof capabilities !== "object") return null;
  const webSearch = (capabilities as Record<string, unknown>).web_search;
  if (!webSearch || typeof webSearch !== "object") return null;
  const adapter = (webSearch as Record<string, unknown>).adapter;
  return typeof adapter === "string" && adapter ? adapter : null;
}

function declaredApi(entry: unknown): string {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) return "chat";
  const api = (entry as Record<string, unknown>).api;
  return typeof api === "string" ? api.toLowerCase().trim() : "chat";
}

export function readModelCatalog(metadata: unknown): ModelCatalog {
  const models =
    metadata && typeof metadata === "object" && !Array.isArray(metadata)
      ? (metadata as Record<string, unknown>).models
      : undefined;

  const adapter = readWebSearchAdapter(metadata);

  if (!models || typeof models !== "object" || Array.isArray(models)) {
    // Sem modelos declarados, só a busca de provedor sem modelo (Tavily) tem o que escolher.
    return adapter && !WEB_SEARCH_LLM_ADAPTERS.includes(adapter)
      ? { ...EMPTY_MODEL_CATALOG, web_search: [WEB_SEARCH_MODEL_PLACEHOLDER] }
      : EMPTY_MODEL_CATALOG;
  }

  const catalog: ModelCatalog = {
    conversational: [],
    embedding: [],
    transcription: [],
    web_search: [],
  };

  for (const [model, entry] of Object.entries(models as Record<string, unknown>)) {
    const api = declaredApi(entry);
    if (api === "embedding") catalog.embedding.push(model);
    else if (api === "transcription") catalog.transcription.push(model);
    else catalog.conversational.push(model);
  }

  catalog.conversational.sort();
  catalog.embedding.sort();
  catalog.transcription.sort();

  // Busca web (AB#86885): no Azure (tool `web_search` da Responses API) e no OpenRouter
  // (server tool `openrouter:web_search`) quem pesquisa é um modelo conversacional; na Tavily
  // e na DeepSeek não há modelo a escolher.
  if (WEB_SEARCH_LLM_ADAPTERS.includes(adapter ?? "")) catalog.web_search = [...catalog.conversational];
  else if (adapter) catalog.web_search = [WEB_SEARCH_MODEL_PLACEHOLDER];

  return catalog;
}
